import type { Level } from "../levels";
import type { Image } from "./image";

export const ArticleIdeaStatus = {
  PENDING: "pending",
  GENERATING: "generating",
  GENERATED: "generated",
} as const;

export type ArticleIdeaStatus = (typeof ArticleIdeaStatus)[keyof typeof ArticleIdeaStatus];

export type KeypointIdea = {
  title: string;
  description: string;
};

export type ArticleIdea = {
  authorAlias: string;
  id: string;
  title: string;
  description: string;
  keyPoints: KeypointIdea[];
  articleId?: string;
  aiModel: string;
  status: ArticleIdeaStatus;
  createdAt: number;
  updatedAt: number;
};

export const ArticleStatus = {
  DRAFT: "draft",
  PENDING_AUDIO: "pending_audio",
  PENDING_PUBLISH: "pending_publish",
  PUBLISHED: "published",
  ARCHIVED: "archived",
} as const;

export type ArticleStatus = (typeof ArticleStatus)[keyof typeof ArticleStatus];

export type ArticleMeta = {
  authorAlias: string;
  id: string;
  ideaId: string;
  /** /authorAlias/<slug(title)>-id */
  path: string;
  image?: Image;
  title: string;
  tagline: string;
  category: string;
  subcategory: string;
  subjects: string[];
  level: Level;
  tags: string[];
  wordCount: number;
  readingMinutes: number;
  aiModel: string;
  createdAt: number;
  updatedAt: number;
  /** Not nullable because it is used as a GSI sort key. */
  publishedAt: number;
  status: ArticleStatus;
  unsplashDownloaded?: boolean;
};

export type KeyPoint = {
  title: string;
  paragraphs: string[];
  takeaway: string;
  image?: Image;
};

export type Article = {
  authorAlias: string;
  id: string;
  summary: string[];
  introduction: string[];
  keyPoints: KeyPoint[];
  conclusion: string[];
  disclaimer?: boolean;
  disclaimerReasons?: string[];
  metaDescription: string;
  searchKeywords: string[];
};

export type ArticleId = {
  alias: string;
  articleId: string;
};

export const ArticleView = {
  ALL: "all",
  POPULAR: "popular",
  RECOMMENDED: "recommended",
  FEED: "feed",
  READ: "read",
  AUTHOR: "author",
  COLLECTION: "collection",
} as const;

export type ArticleView = (typeof ArticleView)[keyof typeof ArticleView];

export const ArticlePopularityPartition = {
  GLOBAL: "global",
  AUTHOR: "author",
  CATEGORY: "category",
  SUBCATEGORY: "subcategory",
} as const;

export type ArticlePopularityPartition = (typeof ArticlePopularityPartition)[keyof typeof ArticlePopularityPartition];

export const ArticlePopularityPeriod = {
  ALL_TIME: "allTime",
  DAILY: "day",
  WEEKLY: "week",
  MONTHLY: "month",
  YEARLY: "year",
} as const;

export type ArticlePopularityPeriod = (typeof ArticlePopularityPeriod)[keyof typeof ArticlePopularityPeriod];

export type ArticleFilter = {
  category?: string;
  subcategory?: string;
  author?: string;
  collectionId?: string;
  popularityPartition?: ArticlePopularityPartition;
  popularityPeriod?: ArticlePopularityPeriod;

  // Only used by the admin UI
  status?: ArticleStatus;
  level?: Level;
  keyword?: string;
};

export type ArticleStatsState = {
  authorAlias: string;
  articleId: string;
  requiresUpdate: boolean;
  lastActivityTimestamp: number;
  /** Last update of this record, not of the article stats. */
  updatedAt: number;
};

/** Always "YYYY-MM-DD". TTL 93 days (90-day rolling window plus a 3-day buffer). */
export type ArticleStatsPeriod = string;

export type ArticleStats = {
  authorAlias: string;
  articleId: string;
  period: ArticleStatsPeriod;
  viewCount: number;
  readCount: number;
  updatedAt: number;
  ttl?: number;
};

export type ArticlePopularity = {
  authorAlias: string;
  articleId: string;
  /**
   * Rolling 90-day score (viewCount * 1 + readCount * 5 over the trailing 90 days). An article with
   * no activity in that window has a score of 0 and drops out of ranked results, on purpose.
   */
  score: number;
  updatedAt: number;
};
